import fs from 'fs-extra';
import { join, relative } from 'path';
import {
  DOCKER_ENV_REL_PATH,
  ENV_DP_PASSWORD,
  ENV_DP_PFX,
  ENV_DP_PREVIOUS_PFX,
  ENV_JWT_SECRET,
  parseDotEnv,
  readCertificateInfoComment,
} from './docker-env.js';
import { readProjectSmartStackVersion, supportsEmptyCertificateSlot } from './smartstack-version.js';

// ============================================================================
// DataProtection — what `ss doctor` and `ss upgrade` check in a project
//
// The platform refuses to start outside Development/Local when its persisted
// key ring is not encrypted by DataProtection:Certificate, and is dropping the
// `RequireEncryptedKeyRing=false` escape hatch. These checks find, statically:
//   - every place that still sets the opt-out (appsettings, compose, .env);
//   - a docker stack whose compose expects secrets docker-images/.env lacks;
//   - a docker stack that provides no certificate at all;
//   - a rotation left half-done (previous certificate parked but not passed on).
// Nothing here reads key material: presence checks and public metadata only.
// ============================================================================

export interface KeyRingOptOut {
  /** Project-relative POSIX path. */
  file: string;
  line: number | null;
}

export interface DataProtectionCheck {
  name: string;
  status: 'ok' | 'warning' | 'error' | 'info';
  message: string;
  details?: string;
  fix?: string;
  /** `ss doctor --fix` may provision docker-images/.env for this check. */
  provisionsDocker?: boolean;
}

const SKIP_DIRS = new Set([
  'node_modules', 'bin', 'obj', '.git', '.vs', '.vscode', '.idea', 'dist', 'TestResults', '.claude', 'coverage',
]);
const MAX_DEPTH = 6;

const ENV_FORM_RE = /DataProtection(?:__|:)RequireEncryptedKeyRing["']?\s*[=:]\s*["']?false\b/i;
const JSON_FALLBACK_RE = /"RequireEncryptedKeyRing"\s*:\s*(?:false|"false")/i;

const isAppSettings = (name: string): boolean => /^appsettings(\..+)?\.json$/i.test(name);
const isComposeFile = (name: string): boolean => /^(docker-)?compose(\..+)?\.ya?ml$/i.test(name);
const isEnvFile = (name: string): boolean => /^\.env(\..+)?$/i.test(name) || /\.env$/i.test(name);

async function walk(dir: string, depth: number, visit: (path: string, name: string) => Promise<void>): Promise<void> {
  if (depth > MAX_DEPTH) return;
  let entries: fs.Dirent[];
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return;
  }
  for (const entry of entries) {
    const path = join(dir, entry.name);
    if (entry.isDirectory()) {
      if (!SKIP_DIRS.has(entry.name)) await walk(path, depth + 1, visit);
    } else if (entry.isFile()) {
      await visit(path, entry.name);
    }
  }
}

function getCaseInsensitive(obj: unknown, key: string): unknown {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return undefined;
  const match = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase());
  return match === undefined ? undefined : (obj as Record<string, unknown>)[match];
}

/** True when an appsettings document opts out of key-ring encryption. */
export function appSettingsOptsOut(content: string): boolean {
  try {
    const json = JSON.parse(content.replace(/^﻿/, '')) as unknown;
    const value = getCaseInsensitive(getCaseInsensitive(json, 'DataProtection'), 'RequireEncryptedKeyRing');
    return value === false || (typeof value === 'string' && value.trim().toLowerCase() === 'false');
  } catch {
    // appsettings tolerate comments/trailing commas that JSON.parse rejects.
    return JSON_FALLBACK_RE.test(content);
  }
}

/** Line numbers (1-based) of an env-var-form opt-out, comments ignored. */
export function envFormOptOutLines(content: string): number[] {
  const lines: number[] = [];
  content.split(/\r?\n/).forEach((line, i) => {
    if (/^\s*#/.test(line)) return;
    if (ENV_FORM_RE.test(line)) lines.push(i + 1);
  });
  return lines;
}

/** Every place under the project that sets RequireEncryptedKeyRing=false. */
export async function findKeyRingOptOuts(projectDir: string): Promise<KeyRingOptOut[]> {
  const found: KeyRingOptOut[] = [];
  await walk(projectDir, 0, async (path, name) => {
    const rel = relative(projectDir, path).replace(/\\/g, '/');
    if (isAppSettings(name)) {
      const content = await fs.readFile(path, 'utf-8');
      if (appSettingsOptsOut(content)) found.push({ file: rel, line: null });
    } else if (isComposeFile(name) || isEnvFile(name)) {
      const content = await fs.readFile(path, 'utf-8');
      for (const line of envFormOptOutLines(content)) found.push({ file: rel, line });
    }
  });
  return found;
}

/** The compose files of the docker stack (docker-images/). */
export async function findDockerComposeFiles(projectDir: string): Promise<string[]> {
  const dir = join(projectDir, 'docker-images');
  if (!(await fs.pathExists(dir))) return [];
  return (await fs.readdir(dir)).filter(isComposeFile).map((name) => join(dir, name));
}

/** Non-comment lines only: a commented-out mapping provides nothing. */
function activeText(content: string): string {
  return content.split(/\r?\n/).filter((l) => !/^\s*#/.test(l)).join('\n');
}

const SLOT_INTERPOLATION_RE = /DataProtection__AdditionalCertificates__(\d+)__Pfx["']?\s*[=:]\s*["']?\$\{(\w+)([^}]*)\}/;
const SLOT_LITERAL_EMPTY_RE = /DataProtection__AdditionalCertificates__(\d+)__Pfx["']?\s*[=:]\s*["']?\s*$/;

/**
 * Indexes of the AdditionalCertificates entries that resolve EMPTY: a literal
 * empty value, or an interpolation that may be empty (`${VAR:-}`, `${VAR}` —
 * never `${VAR:?…}`, which stops compose instead) whose variable is blank.
 */
export function emptyCertificateSlots(compose: string, isBlank: (variable: string) => boolean): number[] {
  const slots: number[] = [];
  for (const line of compose.split(/\r?\n/)) {
    if (/^\s*#/.test(line)) continue;
    const interpolated = SLOT_INTERPOLATION_RE.exec(line);
    if (interpolated) {
      const [, index, variable, modifier] = interpolated;
      if (!modifier.includes('?') && isBlank(variable)) slots.push(Number(index));
      continue;
    }
    const literal = SLOT_LITERAL_EMPTY_RE.exec(line.replace(/['"]\s*$/, ''));
    if (literal) slots.push(Number(literal[1]));
  }
  return slots;
}

const DEV_ENVIRONMENT_RE = /ASPNETCORE_ENVIRONMENT["']?\s*[=:]\s*["']?(Development|Local)\b/i;
const EXPIRY_WARNING_DAYS = 90;
const DOCKER_FIX = 'ss secrets dataprotection --docker';

function formatOptOuts(optOuts: KeyRingOptOut[]): string {
  return optOuts.map((o) => (o.line ? `${o.file}:${o.line}` : o.file)).join(', ');
}

/**
 * A generated project: its .smartstack/config.json is a PROJECT config, or it
 * has a docker stack. The home directory also holds a .smartstack/config.json —
 * the CLI's own settings — and must not be walked as a project.
 */
export async function isSmartStackProject(projectDir: string): Promise<boolean> {
  if (await fs.pathExists(join(projectDir, 'docker-images'))) return true;
  try {
    const config = await fs.readJson(join(projectDir, '.smartstack', 'config.json')) as Record<string, unknown>;
    return typeof config.baseNamespace === 'string' || typeof config.projectType === 'string';
  } catch {
    return false;
  }
}

/**
 * The DataProtection checks for one project. Returns [] when `projectDir` is not
 * a SmartStack project.
 */
export async function diagnoseDataProtection(projectDir: string, now: Date = new Date()): Promise<DataProtectionCheck[]> {
  if (!(await isSmartStackProject(projectDir))) return [];

  const checks: DataProtectionCheck[] = [];

  const optOuts = await findKeyRingOptOuts(projectDir);
  if (optOuts.length > 0) {
    const more = optOuts.length > 1 ? ` (+${optOuts.length - 1})` : '';
    checks.push({
      name: 'DataProtection opt-out',
      status: 'error',
      message: `RequireEncryptedKeyRing=false in ${formatOptOuts(optOuts.slice(0, 1))}${more} — refused outside Development/Local`,
      details: `${formatOptOuts(optOuts)}. The platform no longer accepts this opt-out: remove it and provide a certificate.`,
      fix: `remove RequireEncryptedKeyRing=false, then ${DOCKER_FIX} (docker) or ss secrets dataprotection --env production (deployment)`,
    });
  }

  const composeFiles = await findDockerComposeFiles(projectDir);
  if (composeFiles.length > 0) {
    const compose = activeText((await Promise.all(composeFiles.map((f) => fs.readFile(f, 'utf-8')))).join('\n'));
    const envPath = join(projectDir, ...DOCKER_ENV_REL_PATH.split('/'));
    const envContent = (await fs.pathExists(envPath)) ? await fs.readFile(envPath, 'utf-8') : '';
    const env = parseDotEnv(envContent);
    const blank = (key: string) => (env.get(key) ?? '').trim().length === 0;

    const missing = [ENV_DP_PFX, ENV_DP_PASSWORD, ENV_JWT_SECRET].filter((key) => compose.includes(key) && blank(key));
    const providesCertificate = /DataProtection__Certificate__Pfx/.test(compose);

    // An AdditionalCertificates entry that resolves EMPTY is a slot the platform ignores
    // from 3.70 — and a startup failure before it ("AdditionalCertificates:0:Pfx is required").
    if (emptyCertificateSlots(compose, blank).length > 0) {
      const version = await readProjectSmartStackVersion(projectDir);
      if (!supportsEmptyCertificateSlot(version)) {
        checks.push({
          name: 'DataProtection (docker slot)',
          status: version ? 'error' : 'warning',
          message: version
            ? `Empty AdditionalCertificates slot — SmartStack ${version} refuses it at startup (allowed from 3.70)`
            : 'Empty AdditionalCertificates slot — refused at startup before SmartStack 3.70 (project version unknown)',
          details:
            'docker-compose.yml maps DataProtection__AdditionalCertificates__N__Pfx to a variable that is empty in ' +
            `${DOCKER_ENV_REL_PATH}. Upgrade to SmartStack >= 3.70 (ss upgrade), or remove the slot lines until a ` +
            'previous certificate actually exists.',
          fix: 'ss upgrade (SmartStack >= 3.70), or remove the empty AdditionalCertificates lines',
        });
      }
    }

    if (missing.length > 0) {
      checks.push({
        name: 'DataProtection (docker)',
        status: 'error',
        message: `${DOCKER_ENV_REL_PATH} lacks ${missing.join(', ')} — the stack cannot start`,
        details: 'docker-compose.yml requires them; they are provisioned per machine and never committed.',
        fix: DOCKER_FIX,
        provisionsDocker: true,
      });
    } else if (!providesCertificate) {
      const devStack = DEV_ENVIRONMENT_RE.test(compose);
      checks.push({
        name: 'DataProtection (docker)',
        status: devStack ? 'info' : 'warning',
        message: devStack
          ? 'Docker stack runs as Development/Local — exempt from the certificate'
          : 'Docker stack provides no DataProtection certificate — the backend refuses to start',
        details: devStack
          ? undefined
          : 'docker-compose.yml does not map DataProtection__Certificate__Pfx/Password. ss upgrade updates an unmodified ' +
            'compose file; an edited one needs the two lines of templates/project/docker-compose.yml.template.',
        fix: devStack ? undefined : `ss upgrade (or add the DataProtection__Certificate__* lines), then ${DOCKER_FIX}`,
      });
    } else if (!blank(ENV_DP_PREVIOUS_PFX) && !compose.includes(ENV_DP_PREVIOUS_PFX)) {
      checks.push({
        name: 'DataProtection (docker)',
        status: 'error',
        message: 'Rotation incomplete — the previous certificate is parked but not passed to the backend',
        details:
          `${DOCKER_ENV_REL_PATH} holds ${ENV_DP_PREVIOUS_PFX}, but docker-compose.yml does not map it onto ` +
          'DataProtection__AdditionalCertificates__0__Pfx/Password: the keys it encrypted are unreadable.',
        fix: 'add the AdditionalCertificates lines printed by `ss secrets dataprotection --rotate` to the backend environment',
      });
    } else {
      const info = readCertificateInfoComment(envContent);
      const daysLeft = info ? Math.floor((Date.parse(info.notAfter) - now.getTime()) / 86_400_000) : null;
      const expiry = info ? info.notAfter.slice(0, 10) : null;
      checks.push({
        name: 'DataProtection (docker)',
        status: daysLeft !== null && daysLeft < 0 ? 'error' : daysLeft !== null && daysLeft < EXPIRY_WARNING_DAYS ? 'warning' : 'ok',
        message:
          daysLeft === null
            ? 'Certificate provisioned in docker-images/.env'
            : daysLeft < 0
              ? `Certificate expired on ${expiry}`
              : `Certificate provisioned (expires ${expiry})`,
        details: info ? `thumbprint ${info.thumbprint}` : undefined,
        fix: daysLeft !== null && daysLeft < EXPIRY_WARNING_DAYS ? 'ss secrets dataprotection --docker --rotate' : undefined,
      });
    }
  }

  checks.push({
    name: 'DataProtection (dev)',
    status: 'info',
    message: 'Development/Local exempt — no certificate on a dev machine',
    details: 'Outside dev, inject DataProtection__Certificate__Pfx/Password (ss secrets dataprotection --env <name>).',
  });

  return checks;
}
